import { action, computed, observable } from 'mobx'
import { Model, type ModelProps } from '@/infra/model'
import { unixEpoch } from '@/utils/date-util'
import { Member } from '@/features/iam/members/Member'
import { type User } from '@/features/iam/users/User'
import { Id } from '@/infra/id'
import { once, uniq } from 'lodash-es'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { type DocumentType } from './DocumentType'
import { DocumentStage } from './DocumentStage'
import { DocumentRetention } from './DocumentRetention'
import { type DocumentLinkInput, DocumentLinks } from './DocumentLinks'
import { match } from '@/utils/pattern-match-util'

export type DocumentProps = ModelProps<Document>

/**
 * An uploaded document.
 */
export class Document extends Model<Document> {
  id = Id.none

  @observable accessor workspaceId = Id.none
  @observable accessor title = ''
  @observable accessor description = ''
  @observable accessor archived = false
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()

  @observable accessor owner = Member.none
  @observable accessor author = Member.none
  @observable accessor workspace = Workspace.none

  @observable accessor documentType: DocumentType | null = null

  @observable.ref accessor stage: DocumentStage = DocumentStage.initial
  @observable.ref accessor links: DocumentLinks = DocumentLinks.empty
  @observable.ref accessor retention: DocumentRetention =
    DocumentRetention.active

  /**
   * @deprecated The v5 API has no edit permission concept.
   */
  @observable accessor canEdit = false
  /**
   * @deprecated The v5 API has no ephemeral documents.
   */
  @observable accessor ephemeral = false

  static get none() {
    return lazyNone()
  }

  @computed
  get downloadable() {
    return this.stage.type === 'Released' && this.retention.type === 'Active'
  }

  /**
   * Whether the document is currently being processed.
   */
  @computed
  get isProcessing() {
    return (
      this.stage.type === 'Preprocessing' ||
      this.stage.type === 'AttestationScreening'
    )
  }

  /**
   * Whether the document requires a person to verify it before release.
   */
  @computed
  get isManualAttestationRequired() {
    return this.stage.type === 'ManualAttestationRequired'
  }

  /**
   * Whether the document's contents have been purged.
   */
  @computed
  get isPurged() {
    return this.retention.type === 'Purged'
  }

  /**
   * The IDs of the jobs this document is linked to.
   */
  @computed
  get jobIds() {
    return this.links.job.jobIds
  }

  /**
   * Whether the document is linked to the specified job.
   */
  isLinkedToJob(jobId: Id): boolean {
    if (!jobId) {
      return false
    }

    return this.jobIds.includes(jobId)
  }

  /**
   * Whether the document has the specified link.
   */
  hasLink(link: DocumentLinkInput): boolean {
    return match(link, 'type', {
      Job: (jobLink) => this.links.job.jobIds.includes(jobLink.jobId),
    })
  }

  /**
   * Adds the specified link.
   */
  @action.bound
  addLink(link: DocumentLinkInput) {
    if (this.hasLink(link)) {
      return
    }

    this.links = match(link, 'type', {
      Job: (jobLink): DocumentLinks => ({
        ...this.links,
        job: {
          ...this.links.job,
          jobIds: uniq([...this.links.job.jobIds, jobLink.jobId]),
        },
      }),
    })
  }

  /**
   * Removes the specified link.
   */
  @action.bound
  removeLink(link: DocumentLinkInput) {
    this.links = match(link, 'type', {
      Job: (jobLink): DocumentLinks => ({
        ...this.links,
        job: {
          ...this.links.job,
          jobIds: this.links.job.jobIds.filter((id) => id !== jobLink.jobId),
        },
      }),
    })
  }

  /**
   * The file extension, derived from the title.
   */
  @computed
  get fileType() {
    return this.title.split('.').pop() || 'file'
  }

  /**
   * Whether the user owns the document, which lets them manage its links.
   */
  isOwnedBy(user: User) {
    return this.owner.user.id === user.id
  }
}

const lazyNone = once(() => new Document())
